import React, { useEffect, useState } from 'react';
import { useAnalytics } from '../../../utils/analytics-provider/analytics-provider';
import { Link as GatsbyLink } from 'gatsby';
import styled from 'styled-components';
import Breadcrumb from '../../breadcrumb';
import { ArticleHelpful } from '../article-helpful';
import { useArticleContext } from '../context';
import {
  SectionWrapper,
  InnerSectionWrapper,
  ArticleName,
  ContentWrapper,
  ArticleLoadingContent
} from './article-content.syles';
import { SendInViewEvent } from '../../../helpers/send-in-view-event';
import { SupportCenterParser } from '@carvana/article-renderer/supportcenter';
import states from '@carvana/showroom-forms/Select/UsStatesSelect/states';

const AnalyticEvents = ({ trackEvent, data }) => {
  trackEvent('cjs_signal', { data });
};

const Link = styled(GatsbyLink)`
  text-decoration: underline;
  color: ${(props) => props.theme.color.primary.v200};

  &:hover {
    color: ${(props) => props.theme.color.primary.v200};
  }
`;

// Work-around for custom components that trigger reloads, and we have no control over
// There is an miscommunication between components that useEffect, and the inView func that causes reloads.
const hasCustomComponentThatTriggersReload = ({ article_content } = {}) => {
  const listOfCustomComponentsThatMightMiscommunicateAndTriggerAReloadOnArticlePage = [
    'DeliveryOrPickup',
    'UsStatesSelect'
  ];
  const areAnyOfTheCustomComponentsThatMightMiscommunicateAndTriggerAReloadInsideOfArticleContent = listOfCustomComponentsThatMightMiscommunicateAndTriggerAReloadOnArticlePage.some(
    (componentName) => article_content?.indexOf(componentName) >= 0
  );
  return areAnyOfTheCustomComponentsThatMightMiscommunicateAndTriggerAReloadInsideOfArticleContent;
};

export const ArticleContent = () => {
  const { strapiArticle, user, isLoading } = useArticleContext() || {};
  const { trackEvent } = useAnalytics();
  const parser = SupportCenterParser({ env: process.env.NODE_ENV });
  parser.addUrlComponents({
    internal: ({ url, title }) => <Link to={url}>{title}</Link>
  });
  const { topic, article_content, article_html, article_name } = strapiArticle;
  const [userData, setUserData] = useState({
    date: new Date().toISOString(),
    user: {
      ...user,
      profile: {
        ...(user?.profile || {}),
        address: {
          verifiedDelivery: user?.profile?.address?.filter(
            (x) => x?.kind?.toLowerCase() === 'verifieddelivery'
          )[0]?.address,
          verifiedHome: user?.profile?.address?.filter(
            (x) => x?.kind?.toLowerCase() === 'verifiedhome'
          )[0]?.address,
          verifiedRegistration: user?.profile?.address?.filter(
            (x) => x?.kind?.toLowerCase() === 'verifiedregistration'
          )[0]?.address
        }
      }
    },
    article: strapiArticle
  });

  const UpdateUserData = (data) =>
    setUserData((prevState) => ({
      ...prevState,
      ...data
    }));

  const UpdateUserState = (state) =>
    setUserData((prevState) => ({
      ...prevState,
      ...{
        user: {
          state: state?.toLowerCase()
        }
      }
    }));

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const stateParam = urlParams.get('state');
    const isStateParamTrue = stateParam && states[stateParam?.toUpperCase()] !== undefined;

    if (isStateParamTrue) UpdateUserState(stateParam);
  }, []);

  useEffect(() => {
    if (user.state !== undefined) UpdateUserState(user.state);
  }, [user]);

  if (!isLoading) AnalyticEvents({ trackEvent, data: userData });
  const [ref] = SendInViewEvent({
    isLoading,
    component_name: 'article_content',
    data: { userData, strapiArticle },
    triggerOnce: hasCustomComponentThatTriggersReload({
      article_content: article_content?.data?.article_content
    })
  });

  const renderedArticle =
    strapiArticle &&
    parser.render(article_html || article_content?.data?.article_content, userData, {
      DeliveryOrPickupCallback: UpdateUserData,
      UsStatesSelectCallback: UpdateUserState
    });

  return (
    <SectionWrapper ref={ref}>
      <InnerSectionWrapper>
        <Breadcrumb name={topic?.topic_name} url={topic?.topic_url} />
        <ArticleName>{article_name}</ArticleName>
        <ContentWrapper>
          {isLoading ? <ArticleLoadingContent /> : <>{renderedArticle}</>}
        </ContentWrapper>
        <ArticleHelpful />
      </InnerSectionWrapper>
    </SectionWrapper>
  );
};
